import { getBomTemplate } from "@/api/template";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import HelpButton from "@/components/HelpButton/HelpButton";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { PageHeaderNew } from "@/components/PageHeader/PageHeaderNew";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import Table, { Column } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import useAnalytics from "@/hooks/useAnalytics";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useUrlStateBoolean from "@/hooks/useUrlStateBoolean";
import { DownloadOutlined, ExportOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { useState } from "react";
import { useDashboardYear } from "../../dashboard/hooks/useDateRange";
import HiddenProductsMessage from "../HiddenProductsMessage";
import UploadProductAction from "../actions/UploadProductAction/UploadProductAction";
import { PRODUCTS_BREADCRUMBS } from "../breadcrumbs";
import { Product, proxyColumn } from "../overview/ProductsOverview";
import { useExportProductsBom, useGetProductsBom, useProductStatusPerYear } from "../useProducts";
import styles from "./BillOfMaterials.module.scss";

export default function BillOfMaterialsOverview() {
  useTrackOnLoad("Bill of Materials Overview Viewed");

  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const exportProduct = useExportProductsBom();
  const analytics = useAnalytics();
  const { year } = useDashboardYear();
  const [showUnrelated, setShowUnrelated] = useUrlStateBoolean("showUnrelated", false);

  const columns: Column<Product>[] = [
    {
      key: "external_id",
      label: "ID",
      searchable: true,
      sortable: true,
    },
    {
      key: "name",
      label: "Product",
      maxWidth: "3fr",
      minWidth: "160px",
      sortable: true,
      searchable: true,
    },
    {
      key: "category",
      label: "Category",
      minWidth: "168px",
      sortable: true,
      searchable: true,
      renderer: (product) => product.category || "-",
    },
    proxyColumn,
    {
      key: "bom_status",
      label: "Status",
      minWidth: "150px",
      maxWidth: "150px",
      sortable: true,
      renderer: (product) => (
        <StatusTag
          status={product.bom_status}
          showIcon={false}
        />
      ),
      filterable: true,
    },
  ];

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
      defaultSort: { key: "external_id", order: "ascend" },
    });
  const { data, isLoading, isFetching } = useGetProductsBom({
    year,
    showUnrelated,
    filter,
    sort,
    search,
    pagination,
  });

  const { data: productsYears } = useProductStatusPerYear();

  const onSelectedKeysChange = (keys: number[]) => {
    analytics.track("Product Selected");
    setSelectedProducts((currentProducts) => {
      let productsOnOtherPages = [
        ...currentProducts.filter((x) => !data?.data.map((x) => x.id).includes(x.id)),
      ];
      return [
        ...productsOnOtherPages,
        ...(data?.data.filter((product) => keys.includes(product.id)) || []),
      ];
    });
  };

  const resetTable = () => {
    setPage(1);
    setSelectedProducts([]);
  };

  return (
    <div className={styles.ProductsOverview}>
      <PageHeaderNew
        title="Bill of materials"
        description="Overview of all products that need a bill of materials (BOM). Upload and manage a BOM for each product, individually or in bulk."
        titleRight={
          <PageButtonGroup>
            <UploadProductAction
              renderer={(props) => (
                <Button {...props}>
                  <UploadOutlined /> Upload
                </Button>
              )}
            />
            <FileDownloader
              fileOutputName={`template-bill-of-materials.xlsx`}
              fileUrlFn={getBomTemplate}
              buttonText="Template"
              icon={<DownloadOutlined />}
              onDownload={() => analytics.track("Product Template Downloaded")}
            />
            <FileDownloader
              tooltipText="Export"
              fileOutputName={`export-bill-of-materials.xlsx`}
              fileUrlFn={() => exportProduct.mutateAsync({}).then((data) => data.url)}
              buttonText={"Export"}
              icon={<ExportOutlined />}
              onDownload={() => analytics.track("Products Exported")}
              addTimestampAndCompany={true}
            />
          </PageButtonGroup>
        }
        breadcrumbs={PRODUCTS_BREADCRUMBS}
        breadcrumbsRight={
          <HelpButton
            size="small"
            type="link"
            url="https://help.root-sustainability.com/en/articles/9898491-products"
          />
        }
      />
      <TableHeader
        noRoundedTop
        left={
          <>
            {selectedProducts.length > 0 ? (
              <span className={styles["num-selected"]}>{selectedProducts.length} selected:</span>
            ) : (
              <YearSelector
                disableYears={showUnrelated}
                optionData={productsYears?.map((x) => ({ ...x, status: x.bom_status }))}
                onChange={resetTable}
                right={
                  <Tooltip
                    placement="bottom"
                    title="Show products that are not related to purchase or sales orders"
                  >
                    <>
                      <SwitchWithLabel
                        togglePosition="right"
                        size="small"
                        checked={showUnrelated}
                        onChange={(disabled) => {
                          resetTable();
                          setShowUnrelated(disabled);
                        }}
                      >
                        Year unrelated
                      </SwitchWithLabel>
                    </>
                  </Tooltip>
                }
              />
            )}
            <HiddenProductsMessage />
          </>
        }
        right={
          <StatusTags
            data={data}
            loading={isLoading}
          />
        }
      />
      <Table<Product>
        availableFilters={{
          status: [
            { label: "Complete", value: "complete" },
            { label: "Incomplete", value: "incomplete" },
            { label: "Out of scope", value: "out_of_scope" },
          ],
          relation: [
            { label: "Source", value: "primary_source" },
            { label: "Proxy", value: "proxy" },
            { label: "Unrelated", value: "unrelated" },
          ],
        }}
        loading={isLoading || isFetching}
        rowKey="id"
        data={data?.data}
        onSearchFilterSortPaginate={onSearchFilterSortPaginate}
        onSelectedKeysChange={onSelectedKeysChange}
        selectedKeys={selectedProducts.map((x) => x.id)}
        totalRows={data?.total_count}
        pagination={pagination}
        sort={sort}
        search={search}
        filter={filter}
        // checkable={true}
        columns={columns}
        rowLink={(record) => `/products/${record.id}/bom`}
      />
    </div>
  );
}
